"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { headerNavLinks } from "@/data/navigation";

export default function Navbar() {
  const pathname = usePathname();
  const [currentHash, setCurrentHash] = useState("");

  useEffect(() => {
    const syncHash = () => {
      if (typeof window !== "undefined") {
        setCurrentHash(window.location.hash);
      }
    };

    syncHash();

    const t1 = setTimeout(syncHash, 50);
    const t2 = setTimeout(syncHash, 200);

    window.addEventListener("hashchange", syncHash);
    window.addEventListener("popstate", syncHash);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("hashchange", syncHash);
      window.removeEventListener("popstate", syncHash);
    };
  }, [pathname]);

  const isActive = (href: string) => {
    const normalizedPath = pathname.replace(/\/$/, "");

    if (href === "/") return normalizedPath === "";

    if (href.includes("#")) {
      const [path, hash] = href.split("#");
      const normalizedItemPath = path.replace(/\/$/, "");

      if (hash.toLowerCase() === "equipment") {
        return normalizedPath === "/capabilities" && currentHash.toLowerCase() === "#equipment";
      }

      return normalizedPath === normalizedItemPath && currentHash.toLowerCase() === `#${hash.toLowerCase()}`;
    }

    if (href === "/capabilities") {
      return normalizedPath === "/capabilities" && currentHash.toLowerCase() !== "#equipment";
    }

    return normalizedPath === href.replace(/\/$/, "") || normalizedPath.startsWith(`${href.replace(/\/$/, "")}/`);
  };

  const handleClick = (href: string, e: React.MouseEvent<HTMLAnchorElement>) => {
    const normalizedPath = pathname.replace(/\/$/, "");

    if (href.includes("#equipment") && normalizedPath === "/capabilities") {
      e.preventDefault();
      const el = document.getElementById("equipment");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.pushState(null, "", "/capabilities#equipment");
        setCurrentHash("#equipment");
      }
    } else if (href === "/capabilities" && normalizedPath === "/capabilities") {
      if (currentHash) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
        window.history.pushState(null, "", "/capabilities");
        setCurrentHash("");
      }
    }
  };

  return (
    <nav className="hidden lg:flex items-center justify-center flex-1" aria-label="Main navigation">
      <ul className="flex items-center justify-center gap-6 m-0 p-0 list-none">
        {headerNavLinks.map((item) => {
          const active = isActive(item.href);

          return (
            <li key={item.label}>
              <Link
                href={item.href}
                onClick={(e) => handleClick(item.href, e)}
                aria-current={active ? "page" : undefined}
                className={`relative inline-block font-poppins font-normal text-base leading-6 px-2 py-1 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green rounded
                  after:absolute after:left-2 after:right-2 after:-bottom-0.5 after:h-0.5 after:bg-brand-green after:rounded-full
                  after:origin-left after:transition-transform after:duration-200
                  ${active
                    ? "text-white after:scale-x-100"
                    : "text-white hover:text-brand-cream after:scale-x-0 hover:after:scale-x-100"
                  }`}
              >
                {item.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
